'use client';

/** Number / time formatting helpers shared across the UI. */

let fxRate: Record<string, number> = { USD: 1 };
let fxLoaded = false;

export async function loadFxRates(): Promise<void> {
  if (fxLoaded) return;
  try {
    const cached = localStorage.getItem('glint-fx');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && Date.now() - (parsed.ts || 0) < 6 * 3600_000) {
        fxRate = parsed.rates || { USD: 1 };
        fxLoaded = true;
        return;
      }
    }
    const res = await fetch('https://open.er-api.com/v6/latest/USD');
    const data = await res.json();
    if (data && data.rates) {
      fxRate = data.rates;
      localStorage.setItem('glint-fx', JSON.stringify({ rates: data.rates, ts: Date.now() }));
    }
  } catch {
    /* offline - fall back to USD */
  }
  fxLoaded = true;
}

function getCurrency(): string {
  if (typeof window === 'undefined') return 'USD';
  return localStorage.getItem('glint-currency') || 'USD';
}

function convert(value: number): number {
  const cur = getCurrency();
  if (cur === 'USD') return value;
  const rate = fxRate[cur] || 1;
  return value * rate;
}

export function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const v = convert(value);
  if (v === 0) return '0';
  const sign = v < 0 ? '−' : ''; // U+2212, always before the currency symbol
  const abs = Math.abs(v);
  const cur = getCurrency();
  const symbol = cur === 'USD' ? '$' : cur === 'CNY' ? '¥' : '';
  if (abs >= 1000) return sign + symbol + abs.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (abs >= 1) return sign + symbol + abs.toLocaleString('en-US', { maximumFractionDigits: 4 });
  if (abs >= 0.0000001)
    return sign + symbol + abs.toLocaleString('en-US', { maximumFractionDigits: 10 });
  return sign + symbol + abs.toExponential(4);
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

export function formatVolume(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const v = convert(value);
  const cur = getCurrency();
  const sym = cur === 'USD' ? '$' : cur === 'CNY' ? '¥' : '';
  // AUD03/AUD22: negative sign (U+2212) always before the currency symbol;
  // rounding on display only, never mutating the underlying value.
  const sign = v < 0 ? '−' : '';
  const abs = Math.abs(v);
  if (abs >= 1e12) return `${sign}${sym}${(abs / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${sign}${sym}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}${sym}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${sym}${(abs / 1e3).toFixed(2)}K`;
  if (abs !== 0 && abs < 0.01) return `${sign}${sym}${abs}`; // small non-zero values must not collapse to $0.00
  return `${sign}${sym}${abs.toFixed(2)}`;
}

/** Plain quantity without currency symbol (e.g. circulating supply). */
export function formatQuantity(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toLocaleString('en-US', { maximumFractionDigits: 6 });
}

export function formatCap(value: number | null | undefined): string {  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const v = convert(value);
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  return `$${v.toFixed(0)}`;
}

export function formatTime(ts: number | string | Date): string {
  const d = typeof ts === 'number' || typeof ts === 'string' ? new Date(ts) : ts;
  return d.toLocaleTimeString('en-US', { hour12: false });
}

export function formatDateTime(ts: number | string | Date): string {
  const d = typeof ts === 'number' || typeof ts === 'string' ? new Date(ts) : ts;
  const off = -d.getTimezoneOffset() / 60;
  const tz = off === 0 ? 'UTC+0' : `UTC${off > 0 ? '+' : ''}${off}`;
  return `${d.toLocaleString('en-US', { hour12: false })} ${tz}`;
}

export function getRefreshMs(defaultSec: number): number {
  if (typeof window === 'undefined') return defaultSec * 1000;
  const s = localStorage.getItem('ailumix-refresh');
  return s ? Number(s) * 1000 : defaultSec * 1000;
}
